import React from 'react'
import Alert from '@mui/material/Alert'
import { trim } from 'lodash'
import { useAPI } from 'contexts/APIProvider'
import CQCLocation from 'logic/CQCLocation'
import AutoCompleteSelectField from 'components/AutoCompleteSelectField'

interface CQCLocationSelectorProps {
  label: string
  value: CQCLocation | null
  onChange: (newValue: CQCLocation | null) => void
  errors?: string[]
  helperText?: string
}

const optionLabel = (location: CQCLocation) => `${location.name} (${location.address}, ${location.postcode})`

const CQCLocationSelector: React.FC<CQCLocationSelectorProps> = ({ label, value, onChange, errors, helperText }) => {
  const { api } = useAPI()
  const [results, setResults] = React.useState<CQCLocation[]>([])
  const [fetchFailed, setFetchFailed] = React.useState<boolean>(false)
  // Refs so fetchOptions stays stable (AutoCompleteSelectField rebuilds its debounce when it changes)
  const selected = React.useRef(value)
  selected.current = value
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange
  // Whether the user has searched, to tell a deleted input apart from MUI resetting it to ''
  // when a value seeded from the practice record isn't among the fetched options
  const searched = React.useRef(false)

  const fetchOptions = React.useCallback(async (postcode: string) => {
    // After selecting, MUI resets the input to the option label; don't search for that
    if (selected.current && postcode === optionLabel(selected.current)) return
    if (!trim(postcode)) {
      if (selected.current && searched.current) onChangeRef.current(null)
      setResults([])
      setFetchFailed(false)
      return
    }
    searched.current = true
    // The text no longer matches the selection, so nothing is selected any more
    if (selected.current) onChangeRef.current(null)
    try {
      setResults(await api.listCQCLocations({ postcode, coordinates_available: true, is_stale: false }))
      setFetchFailed(false)
    } catch (e) {
      console.error('error fetching CQC locations', e)
      setFetchFailed(true)
    }
  }, [api])

  const options = React.useMemo(() => {
    return results.map(location => ({
      value: location.location_id,
      label: optionLabel(location),
    }))
  }, [results])

  return (
    <React.Fragment>
      <AutoCompleteSelectField
        label={label}
        options={options}
        fetchOptions={fetchOptions}
        value={value?.location_id ?? ''}
        onChange={(locationId) => onChange(results.find(r => r.location_id === locationId) ?? null)}
        errors={errors}
        helperText={fetchFailed
          ? "Couldn't check the CQC register — please try again."
          : helperText ?? 'If your practice is not listed, or if you are from Scotland, Wales or Northern Ireland, contact support.'}
      />
      {value ? (
        <Alert severity='info' sx={{ whiteSpace: 'pre-line' }}>
          {`CQC ID: ${value.location_id}\nName: ${value.name}\nAddress: ${value.address}\nPostcode: ${value.postcode}\nCity: ${value.city}`}
        </Alert>
      ) : null}
    </React.Fragment>
  )
}

export default CQCLocationSelector
